import {
  Bookmark,
  Boxes,
  Cable,
  Command,
  Eye,
  FileText,
  FolderKanban,
  GitCompare,
  Info,
  LayoutDashboard,
  Loader2,
  Scale,
  ScrollText,
  Settings,
  ShieldCheck,
  SquarePen,
  Upload,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { OPEN_COMMAND_EVENT } from "@/components/AppChrome";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Footer } from "@/components/Footer";
import { ProfileSwitcher } from "@/components/Profiles/ProfileSwitcher";
import { InstallButton } from "@/components/pwa/InstallButton";
import { QuotaWatcher } from "@/components/Quota/QuotaWatcher";
import { ThemeToggle } from "@/components/Settings/ThemeToggle";
import { Button } from "@/components/ui/button";
import { useProfilesContext } from "@/context/ProfileContext";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  /** i18n key in the `navigation` namespace (falls back to `label`). */
  labelKey?: string;
  icon: LucideIcon;
  /** Only shown when a profile is active. */
  requiresProfile?: boolean;
}

interface NavSection {
  heading: string;
  /** i18n key in the `navigation` namespace (falls back to `heading`). */
  headingKey?: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    heading: "Workspace",
    headingKey: "sections.workspace",
    items: [
      { to: "/profiles", label: "Profiles", labelKey: "items.profiles", icon: FolderKanban },
      { to: "/dashboard", label: "Dashboard", labelKey: "items.dashboard", icon: LayoutDashboard, requiresProfile: true },
    ],
  },
  {
    heading: "Tools",
    headingKey: "sections.tools",
    items: [
      { to: "/upload", label: "Upload", labelKey: "items.upload", icon: Upload },
      { to: "/compare", label: "Compare", labelKey: "items.compare", icon: GitCompare },
      { to: "/quotes", label: "Quotes", labelKey: "items.quotes", icon: FileText },
      { to: "/bulk-edit", label: "Bulk Edit", labelKey: "items.bulkEdit", icon: SquarePen },
      { to: "/vendors", label: "Vendors", labelKey: "items.vendors", icon: Boxes, requiresProfile: true },
      { to: "/audit-log", label: "Audit Log", labelKey: "items.auditLog", icon: ScrollText },
      { to: "/saved-searches", label: "Saved Searches", labelKey: "items.savedSearches", icon: Bookmark },
    ],
  },
  {
    heading: "System",
    headingKey: "sections.system",
    items: [
      { to: "/settings", label: "Settings", labelKey: "items.settings", icon: Settings },
      { to: "/connections", label: "Connections", labelKey: "items.connections", icon: Cable },
      { to: "/privacy-inspector", label: "Privacy Inspector", labelKey: "items.privacyInspector", icon: ShieldCheck },
      { to: "/transparency", label: "Transparency", labelKey: "items.transparency", icon: Eye },
      { to: "/privacy-showdown", label: "Privacy Showdown", labelKey: "items.privacyShowdown", icon: Scale },
      { to: "/about", label: "About", labelKey: "items.about", icon: Info },
    ],
  },
];

/** Bottom-nav items for mobile (curated subset; full nav via command palette). */
const MOBILE_ITEMS: NavItem[] = [
  { to: "/profiles", label: "Profiles", icon: FolderKanban },
  { to: "/upload", label: "Upload", icon: Upload },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
];

function openCommandPalette(): void {
  window.dispatchEvent(new CustomEvent(OPEN_COMMAND_EVENT));
}

const PAGE_FALLBACK = (
  <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
    Loading…
  </div>
);

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
    isActive
      ? "bg-primary/10 text-primary"
      : "text-muted-foreground hover:bg-accent hover:text-foreground",
  );
}

/** Responsive shell: left sidebar on desktop, top bar + bottom nav on mobile. */
export function SidebarLayout(): JSX.Element {
  const location = useLocation();
  const { activeProfile } = useProfilesContext();
  const { t } = useTranslation("navigation");

  const visibleSections = SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.requiresProfile || activeProfile !== null),
  }));

  return (
    <div className="flex min-h-screen w-full flex-col bg-background text-foreground md:flex-row">
      <aside className="hidden w-60 shrink-0 flex-col border-r md:flex">
        <div className="flex items-center gap-2 px-5 py-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary font-bold text-primary-foreground">
            CK
          </div>
          <div>
            <div className="font-semibold leading-tight">CatalogKit</div>
            <div className="text-xs text-muted-foreground">PSA catalog cleaner</div>
          </div>
        </div>

        <div className="px-3 pb-2">
          <ProfileSwitcher />
        </div>

        <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-2">
          {visibleSections.map((section) => (
            <div key={section.heading} className="space-y-1">
              <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
                {section.headingKey ? t(section.headingKey, section.heading) : section.heading}
              </p>
              {section.items.map(({ to, label, labelKey, icon: Icon }) => (
                <NavLink key={to} to={to} className={navLinkClass}>
                  <Icon className="h-4 w-4" />
                  {labelKey ? t(labelKey, label) : label}
                </NavLink>
              ))}
            </div>
          ))}
          <button
            type="button"
            onClick={openCommandPalette}
            className="mt-2 flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <span className="flex items-center gap-2">
              <Command className="h-4 w-4" />
              Commands
            </span>
            <kbd className="rounded border bg-muted px-1.5 text-xs">⌘K</kbd>
          </button>
        </nav>

        <div className="flex items-center justify-between border-t px-4 py-3">
          <InstallButton />
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Theme</span>
            <ThemeToggle />
          </div>
        </div>
      </aside>

      <header className="flex items-center gap-2 border-b px-4 py-3 md:hidden">
        <NavLink to="/" className="flex shrink-0 items-center gap-2" aria-label="CatalogKit home">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-xs font-bold text-primary-foreground">
            CK
          </div>
        </NavLink>
        <div className="min-w-0 flex-1">
          <ProfileSwitcher />
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Open command palette"
          onClick={openCommandPalette}
        >
          <Command className="h-4 w-4" />
        </Button>
        <ThemeToggle />
      </header>

      <div className="flex min-w-0 flex-1 flex-col pb-16 md:pb-0">
        <main className="flex-1 p-4 md:p-6">
          <QuotaWatcher />
          <ErrorBoundary key={location.pathname}>
            <Suspense fallback={PAGE_FALLBACK}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
        <Footer />
      </div>

      <nav
        className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-4 border-t bg-background/95 backdrop-blur md:hidden"
        aria-label="Primary"
      >
        {MOBILE_ITEMS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                "flex flex-col items-center gap-0.5 py-2 text-xs transition-colors",
                isActive ? "text-primary" : "text-muted-foreground",
              )
            }
          >
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={openCommandPalette}
          className="flex flex-col items-center gap-0.5 py-2 text-xs text-muted-foreground transition-colors"
        >
          <Command className="h-5 w-5" />
          More
        </button>
      </nav>
    </div>
  );
}
